/**
 * open-library.ts — shared helpers to open the LocalIndex and the configured
 * Catalog source.
 *
 * Lazy-loads the `@skaile/workspaces/library` subpath and returns the
 * LocalIndex instance (backed by `@libsql/client`; runs on Node and Bun).
 * The catalog-source helper reads
 * `~/.skaile/config.yaml` (and project-level overlay if `projectDir` is given)
 * to decide between {@link RemoteCatalogSource} (default — points at
 * `https://skaile.store`) and a {@link LocalCatalogSource} bound to the most
 * recent local library registered via `skaile source add`.
 */

import { logErr } from "./helpers.ts";
import { createFullRegistry } from "./open-registry.ts";

/** Thrown by {@link openCatalogSource} when `catalog.url` is the `local` sentinel. */
export class LocalCatalogDisabledError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LocalCatalogDisabledError";
  }
}

/**
 * Open the LocalIndex with the full kind registry. Exits with code 1
 * if the library directory cannot be created.
 *
 * @returns A `LocalIndex` instance ready for use.
 * @docLink cli/dev-guide#open-library
 */
export async function openLibrary() {
  try {
    const { LocalIndex } = await import("@skaile/workspaces/library");
    return new LocalIndex({ kindRegistry: createFullRegistry() });
  } catch (err) {
    logErr(`Could not open LocalIndex: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }
}

/**
 * Resolve the configured **remote** Catalog source for the CLI.
 *
 * Reads `~/.skaile/config.yaml` (plus the optional project-level overlay) and
 * returns a {@link RemoteCatalogSource} pointing at `catalog.url` (default:
 * `https://skaile.store`). The `cache_ttl: 0` config flag flips the source
 * into air-gapped mode: network reads are disabled, only `skaile update`
 * performs refreshes.
 *
 * **Local-mode rejection.** If the resolved config has `catalog.url: local`
 * this helper throws — callers wanting the dispatched local-or-remote source
 * must use {@link resolveCatalogSource}. Remote-only consumers like
 * `skaile update --catalog-only` rely on this strict behaviour.
 *
 * @param opts - Project directory (overlays project config) and explicit overrides.
 * @returns A configured {@link RemoteCatalogSource}.
 * @throws When `catalog.url` is the `local` sentinel.
 * @docLink cli/dev-guide#open-library
 */
export async function openCatalogSource(opts?: {
  projectDir?: string;
  baseUrlOverride?: string;
  /** Override the user-config path for tests. */
  userConfigFile?: string;
}): Promise<
  | import("@skaile/workspaces/library").RemoteCatalogSource
  | import("@skaile/workspaces/library").RestCatalogSource
> {
  const { resolveConfig, RemoteCatalogSource, RestCatalogSource, isLocalCatalogUrl } = await import(
    "@skaile/workspaces/library"
  );
  const cfg = resolveConfig({
    projectDir: opts?.projectDir,
    userConfigFile: opts?.userConfigFile,
  });
  const baseUrl = opts?.baseUrlOverride ?? cfg.catalog.url;
  if (isLocalCatalogUrl(baseUrl)) {
    throw new LocalCatalogDisabledError(
      "catalog.url is set to 'local' — remote catalog is disabled. " +
        "Use `skaile source add <path>` + `skaile source sync` for local sources, " +
        "or set `catalog.url: https://skaile.store` in ~/.skaile/config.yaml.",
    );
  }
  const cacheTtlMs = cfg.catalog.cache_ttl * 1000;
  if (cfg.catalog.framing === "rest") {
    return new RestCatalogSource({ baseUrl, cacheTtlMs });
  }
  return new RemoteCatalogSource({ baseUrl, cacheTtlMs });
}

/**
 * Resolved catalog source plus a close handle for releasing any underlying
 * resources (e.g. the `LocalIndex` SQLite connection in local mode).
 *
 * Callers MUST invoke `close()` when done — typically in a `finally` block.
 * For remote-mode sources `close()` is a no-op, but the contract is uniform
 * so consumers don't have to branch.
 *
 * @docLink cli/dev-guide#open-library
 */
export interface ResolvedCatalogSource {
  /** The {@link ICatalogSource} ready for use. */
  source: import("@skaile/workspaces/library").ICatalogSource;
  /** Release any underlying resources (SQLite handle in local mode). */
  close(): void;
}

/**
 * Resolve the configured Catalog source — local or remote — based on
 * `~/.skaile/config.yaml`.
 *
 * Dispatch:
 * - `catalog.url: local` → opens {@link LocalIndex}, picks the most recently
 *   registered local library whose `path` still exists on disk, and returns a
 *   {@link LocalCatalogSource} bound to that library. Throws if no usable
 *   local library is registered.
 * - any URL → returns {@link RemoteCatalogSource} (same as
 *   {@link openCatalogSource}).
 *
 * The returned `close()` releases the underlying `LocalIndex` SQLite handle
 * in local mode (no-op in remote mode). Callers MUST invoke it — typically in
 * a `finally` block — to satisfy `library/CLAUDE.md` § "Notes for Consumers".
 *
 * @param opts - Project directory (overlays project config) and explicit overrides.
 * @returns A `ResolvedCatalogSource` carrying the source and a close handle.
 * @throws When `local` is configured but no usable local library is registered.
 * @docLink cli/dev-guide#open-library
 */
export async function resolveCatalogSource(opts?: {
  projectDir?: string;
  baseUrlOverride?: string;
  /** Override the user-config path for tests. */
  userConfigFile?: string;
}): Promise<ResolvedCatalogSource> {
  const {
    resolveConfig,
    RemoteCatalogSource,
    RestCatalogSource,
    LocalCatalogSource,
    isLocalCatalogUrl,
  } = await import("@skaile/workspaces/library");
  const cfg = resolveConfig({
    projectDir: opts?.projectDir,
    userConfigFile: opts?.userConfigFile,
  });
  const baseUrl = opts?.baseUrlOverride ?? cfg.catalog.url;

  if (isLocalCatalogUrl(baseUrl)) {
    const fs = await import("node:fs");
    const library = await openLibrary();
    try {
      const sources = await library.listSources();
      type LibraryRow = (typeof sources)[number];
      // Type-narrow: `path` is required for local libraries after the filter.
      const usable = sources
        .filter(
          (s): s is LibraryRow & { path: string } =>
            s.backend === "local" && typeof s.path === "string" && s.path.length > 0,
        )
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      if (usable.length === 0) {
        throw new Error(
          "catalog.url is set to 'local' but no local library is registered. " +
            "Run `skaile source add <path>` first, then `skaile source sync`.",
        );
      }

      // Prefer the most recently added library whose path still exists.
      const present = usable.find((s) => fs.existsSync(s.path));
      if (!present) {
        const head = usable[0];
        throw new Error(
          `catalog.url is set to 'local' but every registered local library has a missing path on disk. ` +
            `Most recent: ${head.path} (library ${head.id}). ` +
            `Re-register with \`skaile source add <existing-path>\` or run \`skaile source list\` to inspect.`,
        );
      }

      const source = new LocalCatalogSource(
        library,
        present.id,
        present.path,
        createFullRegistry(),
      );
      return { source, close: () => library.close() };
    } catch (err) {
      // Release the handle on any failure during dispatch — otherwise the
      // SQLite WAL state leaks for the lifetime of the process.
      library.close();
      throw err;
    }
  }

  const cacheTtlMs = cfg.catalog.cache_ttl * 1000;
  const source =
    cfg.catalog.framing === "rest"
      ? new RestCatalogSource({ baseUrl, cacheTtlMs })
      : new RemoteCatalogSource({ baseUrl, cacheTtlMs });
  return { source, close: () => {} };
}

/**
 * Open the LibraryManager bound to the active LocalIndex.
 * Caller owns lifetime — must call `close()` (returned helper closes the
 * underlying LocalIndex).
 *
 * @docLink cli/dev-guide#open-library
 */
export async function openLibraryManager(): Promise<{
  manager: import("@skaile/workspaces/library").LibraryManager;
  library: import("@skaile/workspaces/library").LocalIndex;
  close: () => void;
}> {
  const { LibraryManager } = await import("@skaile/workspaces/library");
  const library = await openLibrary();
  const manager = new LibraryManager(library);
  return { manager, library, close: () => library.close() };
}
